import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { CenteredSpinner } from '../components/Spinner.tsx';
import { useAdminAuth } from '../lib/auth/admin.tsx';
import { AuthSplitLayout } from '../components/AuthSplitLayout.tsx';

// This portal no longer authenticates admins. /admin/login exists only to hand
// the browser to the central l2u-identity login page, which routes @fedex.com
// to Okta and everyone else to an emailed code. The return lands on
// /admin/sso/callback.
export function AdminLogin() {
  const { user, session, loading, accessDenied, signInWithSso } = useAdminAuth();
  const navigate = useNavigate();
  const [error, setError] = useState<string | null>(null);
  const startedRef = useRef(false);

  // Already signed in — never bounce an authenticated admin to the IdP.
  useEffect(() => {
    if (user) navigate('/admin', { replace: true });
  }, [user, navigate]);

  // Refused, and the session is deliberately kept alive (lib/auth/admin.tsx), so
  // the effect below would park on its spinner forever waiting for a `user` that
  // is never coming. Send them to the refusal screen instead.
  useEffect(() => {
    if (accessDenied) {
      navigate('/admin/no-access', { replace: true, state: { reason: accessDenied.reason } });
    }
  }, [accessDenied, navigate]);

  useEffect(() => {
    // Wait for the initial getSession() to settle, otherwise a returning admin
    // with a valid session is redirected before `user` is known.
    //
    // `session` is part of the guard, not just `user`: SsoCallback establishes
    // the session and navigates to /admin, but the provider resolves `user` via
    // an un-awaited loadUser (lib/auth/admin.tsx). AdminShell renders first,
    // sees user=null and bounces here — so without the session check a brand-new
    // sign-in would immediately be sent back through the IdP. A live session
    // with an unresolved user should wait: it resolves to a user, to a 401 that
    // clears the session and re-enables this effect, or to a 403 that the
    // accessDenied effect above routes away — so the wait always terminates.
    if (loading || user || session || startedRef.current) return;

    // An invite link already in flight can land here rather than on
    // /admin/accept-invite, because Supabase falls back to its Site URL when the
    // requested redirectTo is not allow-listed. Forward it instead of starting
    // SSO, which would discard the credential. Recovery links no longer have a
    // destination — /admin/reset-password is deleted and password recovery is
    // l2u-identity's job — so anything credential-bearing goes to accept-invite,
    // which reports what it cannot use.
    const { search, hash } = window.location;
    if (search || hash) {
      const fragment = hash ? new URLSearchParams(hash.replace(/^#/, '')) : null;
      const query = search ? new URLSearchParams(search) : null;
      const hasCredential =
        (fragment?.get('access_token') && fragment.get('refresh_token')) ||
        query?.get('code') ||
        query?.get('token_hash');
      if (hasCredential) {
        startedRef.current = true;
        navigate(`/admin/accept-invite${search}${hash}`, { replace: true });
        return;
      }
    }

    startedRef.current = true;
    void signInWithSso().catch(() => {
      // Only catches failures to *start* the redirect — missing Supabase config,
      // or no URL returned. Under PKCE the authorize URL is built locally with
      // no network call, so an unregistered provider does NOT fail here; it
      // fails at Supabase after we navigate, and comes back either as ?error=
      // (handled by SsoCallback) or as a Supabase error page. Keep this state
      // anyway — an indefinite spinner would be undiagnosable.
      setError(
        'Sign-in is unavailable right now. Please try again, or contact your administrator if this continues.',
      );
    });
  }, [loading, user, session, signInWithSso, navigate]);

  return (
    <AuthSplitLayout variant="admin">
      <div className="flex flex-col gap-6">
        <div className="flex flex-col gap-1.5">
          <h2 className="text-2xl text-slate-900">Admin sign in</h2>
          <p className="text-sm text-slate-500">
            {error ? 'Sign-in unavailable' : 'Redirecting you to sign in…'}
          </p>
        </div>
        {error ? (
          <div className="rounded-lg border border-amber-200 bg-amber-50 px-3.5 py-3 text-sm text-amber-800">
            {error}
          </div>
        ) : (
          <CenteredSpinner />
        )}
      </div>
    </AuthSplitLayout>
  );
}
